import { Link } from '@cloudscape-design/components';

export type ClientProfileProps = {
  url?: string;
  image: string;
  title: string;
  subtitle: string;
  children?: React.ReactNode;
  background?: string; // Optional hex background color
  borderSize?: string; // Optional border size, also squares the image
};

export default function ClientProfile(props: ClientProfileProps) {
  const imageContent = (
    <img
      alt={`${props.title} Logo`}
      className="max-h-64 mb-4 rounded-lg"
      src={props.image}
      style={{
        backgroundColor: props.background,
        padding: props.borderSize,
      }}
    />
  );

  return (
    <div className="client flex flex-col items-center p-4 m-4">
      {props.url ? (
        <Link href={props.url} target="_blank">
          {imageContent}
        </Link>
      ) : (
        imageContent
      )}
      <h3 className="text-xl font-bold text-center">{props.title}</h3>
      <h4 className="text-lg mb-2 text-center">{props.subtitle}</h4>
      {props.children}
    </div>
  );
}

export const CLIENTS = [
  <ClientProfile
    key="zappy"
    background="#000000"
    borderSize="60px"
    image="https://cdn.aiescape.io/zappy/Zappy logo yellow.png"
    subtitle="Stay Positive"
    title="NeedZappy"
    url="https://needzappy.com/"
  >
    <p>
      Zappy rents portable chargers at 2,000+ venues across the United States and Canada, which
      involves many moving parts. We helped them optimize their operations and increase their
      revenue with AI, from inventory management to business intelligence.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="usa-rs"
    background="#ffffff"
    borderSize="60px"
    image="https://cdn.aiescape.io/usa-rs/usa-rs-logo.png"
    subtitle="#KitchenEquipment"
    title="USA Restaurant Suppliers"
    url="https://usa-rs.com/"
  >
    <p>
      Trusted by over 1,000 commercial kitchens, USA Restaurant Suppliers is your go-to partner for
      top-quality equipment and unbeatable service. We supercharged their operations with AI, from
      the development of automated assistants to AI-powered quotes.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="real-time-feedback"
    background="#f0f4fc"
    borderSize="20px"
    image="https://cdn.aiescape.io/real-time-feedback/rt-feedback-logo.svg"
    subtitle="#FeedbackLoop"
    title="Real Time Feedback"
  >
    <p>
      Real Time Feedback is a platform that provides real-time customer feedback to businesses. We
      helped them analyze customer feedback data and generate actionable insights using AI to
      improve their services and customer satisfaction.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="netzero"
    background="#ffffff"
    borderSize="20px"
    image="https://cdn.aiescape.io/netzero/NZ_Horizontal (000000).png"
    subtitle="#DriveLuxury"
    title="Net Zero Fleet and Armouring"
  >
    <p>
      Net Zero is a car brokerage firm that specializes in importing luxury vehicles from all over
      the world. We helped them accelerate their operations and streamline their processes with AI,
      from sourcing inventory to managing customer relationships.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="score"
    image="https://cdn.aiescape.io/score-logo.png"
    subtitle="#WeBrandEverything"
    title="Score Promotions"
    url="https://www.scorepromotions.com/"
  >
    <p>
      As one of North America's largest promotional marketing companies, Score Promotions has a lot
      of data to manage. We used AI to help them analyze their data and optimize their strategies to
      drive growth, forecast inventory, and increase their margins.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="lendwire"
    background="#ffffff"
    borderSize="20px"
    image="https://cdn.aiescape.io/lendwire/lendwire-transparent-logo.png"
    subtitle="The Future of Borrowing is Here"
    title="Lendwire"
    url="https://www.lendwire.com/"
  >
    <p>
      Lendwire is a mortgage broker in Canada that needed help managing their data and improving
      their customer experience. We used AI to optimize their operations, streamline their
      processes, and provide better service to their clients.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="davidson-law"
    background="#ffffff"
    borderSize="40px"
    image="https://cdn.aiescape.io/davidson-law/davidson-logo.webp"
    subtitle="At Your Service"
    title="J. L. Davidson Law"
    url="https://www.jessicadavidsonlaw.com/"
  >
    <p>
      J. L. Davidson Law is a law firm that needed help optimizing their file management and
      operations. We used AI to automate their documentation tasks, save time, and improve the
      accuracy of their work, allowing them to focus on providing high-quality legal services to
      their clients.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="foothill-ventures"
    image="https://cdn.aiescape.io/foothill-ventures/foothill-logo.jpg"
    subtitle="Technical Founders. Early Stage Technology."
    title="Foothill Ventures"
    url="https://www.foothill.ventures/"
  >
    <p>
      Foothill Ventures is a tech-focused venture capital firm that needed help discovering and
      analyzing new investment opportunities. We provided them with custom AI tools to streamline
      their discovery process, identify promising startups, and make data-driven decisions.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="cdc"
    background="#ffffff"
    borderSize="20px"
    image="https://cdn.aiescape.io/cdc-logo.png"
    subtitle="#PublicHealth"
    title="CDC"
    url="https://www.cdc.gov/"
  >
    <p>
      The Centers for Disease Control and Prevention (CDC) is the leading national public health
      institute of the United States. We helped them analyze ongoing projects, collect grant data,
      and improve their internal processes with AI to better serve the public.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="td"
    image="https://cdn.aiescape.io/td-logo2.png"
    subtitle="America's Most Convenient Bank"
    title="TD Bank"
    url="https://www.td.com/"
  >
    <p>
      As one of the top 10 financial services companies in North America, TD Bank greatly benefits
      from AI in their day-to-day operations. We used AI to help them estimate risk, forecast sales,
      and optimize their operations to provide better service to their customers.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="pareto"
    image="https://cdn.aiescape.io/pareto-logo2.jpg"
    subtitle="Premium Training Data"
    title="Pareto"
    url="https://pareto.ai/"
  >
    <p>
      Pareto is a data collection and labeling platform that provides high-quality training data for
      AI. We helped them optimize their data collection processes and improve the quality of their
      training data by consulting on their AI strategy.
    </p>
  </ClientProfile>,
  <ClientProfile
    key="ai-escape"
    image="https://cdn.aiescape.io/box.png"
    subtitle="What Would You Like to Do?"
    title="AI Escape"
    url="https://www.aiescape.io/"
  >
    <p>
      AI Escape is a revolutionary escape room platform that utilizes AI to generate dynamic,
      customized escape rooms. We built AI Escape to satisfy the growing demand for escape room
      experiences and to provide unique, personalized puzzles for our users.
    </p>
  </ClientProfile>,
];
